/**
 * © 2026 Madar.iq — جميع الحقوق محفوظة
 * هذا الملف جزء من منظومة Madar.iq البرمجية.
 * يُحظر نسخه أو تعديله أو توزيعه دون إذن كتابي.
 * All Rights Reserved — Unauthorized use is prohibited.
 */
import React, { useEffect } from 'react';
import { FiX, FiTrash2, FiMinus, FiPlus, FiArrowRight, FiShoppingBag, FiMapPin, FiPhone, FiMail, FiUser, FiTag } from 'react-icons/fi';
import { buildImageUrl } from '../theme1/utils/theme1Helpers';
import { formatNumber } from '../../utils/formatNumber';

const CartSidebar = ({
    tenantId,
    theme,
    cities,
    isMobile,
    cart,
    appliedCoupon,
    couponDiscount,
    subtotal,
    shipping,
    total,
    displayCoupons,
    couponCode,
    setCouponCode,
    couponError,
    applyCouponCode,
    handleApplyCoupon,
    handleUpdateQty,
    handleRemove,
    deliveryForm,
    setDeliveryForm,
    handleCheckoutSubmit,
    setShowAuthModal,
    onClose
}) => {
    // Prefill city from last selection (same pattern as Theme2)
    useEffect(() => {
        if (deliveryForm?.cityId) return;
        const savedCityId = localStorage.getItem("cartSavedCityId") || localStorage.getItem("selectedCityId");
        const savedCityName = localStorage.getItem("cartSavedCityName");
        if (savedCityId) {
            setDeliveryForm(prev => ({
                ...prev,
                cityId: savedCityId,
                cityName: savedCityName || prev.cityName || ""
            }));
        }
    }, [deliveryForm?.cityId, setDeliveryForm]);

    return (
        <div className="fixed inset-0 z-[100] bg-white flex flex-col font-sans overflow-hidden max-h-[100vh]" dir="rtl">
            {/* Header */}
            <div className="h-16 border-b border-gray-100 flex items-center justify-between px-4 sm:px-6 lg:px-8 bg-white sticky top-0 z-10">
                <div className="flex items-center gap-3">
                    <div className={`${theme.bg} p-2 rounded-lg text-white`}>
                        <FiShoppingBag className="text-xl" />
                    </div>
                    <h2 className="text-xl font-bold text-gray-800">سلة المشتريات</h2>
                    <span className="bg-gray-100 px-2 py-0.5 rounded text-xs font-bold text-gray-500">
                        {cart.reduce((sum, item) => sum + (Number(item.quantity || item.qty) || 0), 0)} منتج
                    </span>
                </div>
                <button
                    onClick={onClose}
                    className="p-2 hover:bg-gray-100 rounded-full transition-colors text-gray-500 hover:text-gray-800"
                >
                    <FiX className="text-2xl" />
                </button>
            </div>

            <div className="flex-1 overflow-y-auto bg-gray-50/30">
                <div className="max-w-7xl mx-auto w-full p-4 sm:p-6 lg:p-8">
                    {cart.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-20 text-center">
                            <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mb-6">
                                <FiShoppingBag className="text-4xl text-gray-300" />
                            </div>
                            <h3 className="text-2xl font-bold text-gray-800 mb-2">سلتك فارغة حالياً</h3>
                            <button
                                onClick={onClose}
                                className={`${theme.bg} text-white px-8 py-3 rounded-xl font-bold flex items-center gap-2 hover:shadow-lg transition-all active:scale-95`}
                            >
                                <span>ابدأ التسوق</span>
                                <FiArrowRight className="rotate-180" />
                            </button>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                            {/* Left Side: Items */}
                            <div className="lg:col-span-8 w-full order-1 lg:order-1 space-y-4">
                                <div className="bg-white rounded-2xl p-4 sm:p-6 shadow-sm border border-gray-100">
                                    <div className="divide-y divide-gray-100">
                                        {cart.map((item, idx) => {
                                            const v = item.variants?.[0] || item;
                                            const itemName = item.itemName || item.name || "منتج غير مسمى";
                                            const qty = Number(v.quantity || item.quantity || 1);
                                            const price = Number(v.price || item.price || 0);
                                            const colorName = v.color || item.color || null;
                                            const colorHex = (typeof colorName === 'string' && colorName.startsWith('#')) ? colorName : null;
                                            const sizeValue = v.size || item.size || "";
                                            const categoryName =
                                                item.categoryName ||
                                                item.category ||
                                                v.categoryName ||
                                                v.category ||
                                                item.mainCategoryName ||
                                                "";
                                            const itemImage =
                                                v.imageUrl ||
                                                item.imageUrl ||
                                                item.parentImageUrl ||
                                                item.mainImage ||
                                                item.mainImageUrl ||
                                                item.image;

                                            return (
                                                <div key={`cart-item-${v.variantId || idx}-${idx}`} className="py-4 flex flex-col sm:flex-row gap-4 sm:gap-6 border-b border-gray-100 last:border-0">
                                                    {/* الصورة */}
                                                    <div className="w-full sm:w-24 h-24 bg-gray-50 rounded-2xl overflow-hidden flex-shrink-0 border border-gray-100">
                                                        <img
                                                            src={buildImageUrl(itemImage)}
                                                            alt={itemName}
                                                            className="w-full h-full object-contain p-2"
                                                            onError={(e) => { e.target.src = "https://placehold.co/100x100.png?text=Product"; }}
                                                        />
                                                    </div>

                                                    <div className="flex-1 flex flex-col">
                                                        <div className="flex justify-between items-start">
                                                            <h4 className="font-bold text-gray-800 line-clamp-1">{itemName}</h4>
                                                            <button
                                                                onClick={() => handleRemove(v.variantId || item.variantId)}
                                                                className="text-gray-400 hover:text-red-500 transition-colors p-1"
                                                            >
                                                                <FiTrash2 />
                                                            </button>
                                                        </div>

                                                        <div className="flex flex-wrap gap-2 mt-2">
                                                            {categoryName && (
                                                                <div className="flex items-center gap-1.5 bg-gray-50 px-2 py-1 rounded-md border border-gray-100 shadow-sm">
                                                                    <span className="text-[10px] text-gray-400 font-bold uppercase">القسم:</span>
                                                                    <span className="text-xs font-bold text-gray-700">{categoryName}</span>
                                                                </div>
                                                            )}
                                                            {colorName && (
                                                                <div className="flex items-center gap-1.5 bg-gray-50 px-2 py-1 rounded-md border border-gray-100 shadow-sm">
                                                                    <span className="text-[10px] text-gray-400 font-bold uppercase">اللون:</span>
                                                                    {colorHex ? (
                                                                        <span
                                                                            className="w-4 h-4 rounded-full border border-gray-300 inline-block"
                                                                            style={{ backgroundColor: colorHex }}
                                                                        />
                                                                    ) : (
                                                                        <span className="text-xs font-bold text-gray-700">{colorName}</span>
                                                                    )}
                                                                </div>
                                                            )}
                                                            {sizeValue && (
                                                                <div className="flex items-center gap-1.5 bg-gray-50 px-2 py-1 rounded-md border border-gray-100 shadow-sm">
                                                                    <span className="text-[10px] text-gray-400 font-bold uppercase">المقاس:</span>
                                                                    <span className="text-xs font-bold text-gray-700">{sizeValue}</span>
                                                                </div>
                                                            )}
                                                        </div>

                                                        <div className="mt-4 flex flex-col sm:flex-row justify-between items-start sm:items-end gap-2">
                                                            <div className="flex flex-col gap-1">
                                                                <div className="flex items-center bg-gray-100 rounded-lg p-1 w-fit">
                                                                    <button
                                                                        onClick={() => handleUpdateQty(v.variantId || item.variantId, -1)}
                                                                        className="p-1 hover:text-red-500 transition-colors"
                                                                    >
                                                                        <FiMinus size={14} />
                                                                    </button>
                                                                    <span className="w-8 text-center font-bold text-sm">{qty}</span>
                                                                    <button
                                                                        onClick={() => handleUpdateQty(v.variantId || item.variantId, 1)}
                                                                        disabled={v.stockQty > 0 && qty >= v.stockQty}
                                                                        className={`p-1 transition-colors ${qty >= (v.stockQty || 999) ? 'text-gray-300' : 'hover:text-green-600'}`}
                                                                    >
                                                                        <FiPlus size={14} />
                                                                    </button>
                                                                </div>
                                                                {qty >= (v.stockQty || 999) && (
                                                                    <p className="text-[10px] text-red-500 font-bold mt-1 text-center">وصلت للحد الأقصى للمخزون</p>
                                                                )}
                                                            </div>

                                                            <div className="text-left mt-2 sm:mt-0">
                                                                <p className={`text-lg font-black ${theme.text}`}>
                                                                    {formatNumber(price * qty)}
                                                                    <span className="text-xs mr-1">د.ع</span>
                                                                </p>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>

                            {/* Right Side: Order Summary & Checkout */}
                            <div className="lg:col-span-4 w-full order-2 lg:order-2 mt-6 lg:mt-0 space-y-6">
                                {/* معلومات التوصيل */}
                                <div className="bg-white rounded-2xl p-4 sm:p-6 shadow-sm border border-gray-100">
                                    <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2 uppercase tracking-wide">
                                        <FiMapPin className={`${theme.text}`} />
                                        معلومات التوصيل
                                    </h3>

                                    <div className="space-y-4">
                                        <div className="group relative">
                                            <FiUser className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400" />
                                            <input
                                                type="text"
                                                placeholder="الاسم بالكامل"
                                                value={deliveryForm.name || ''}
                                                onChange={(e) => setDeliveryForm({ ...deliveryForm, name: e.target.value })}
                                                className="w-full bg-gray-50 border border-gray-200 rounded-xl py-3 pr-11 pl-4 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all"
                                            />
                                        </div>
                                        <div className="group relative">
                                            <FiMail className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400" />
                                            <input
                                                type="email"
                                                placeholder="البريد الإلكتروني"
                                                value={deliveryForm.email || ''}
                                                onChange={(e) => setDeliveryForm({ ...deliveryForm, email: e.target.value })}
                                                className="w-full bg-gray-50 border border-gray-200 rounded-xl py-3 pr-11 pl-4 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all"
                                            />
                                        </div>
                                        <div className="group relative">
                                            <FiPhone className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400" />
                                            <input
                                                type="text"
                                                placeholder="رقم الهاتف"
                                                value={deliveryForm.phone || ''}
                                                onChange={(e) => setDeliveryForm({ ...deliveryForm, phone: e.target.value })}
                                                className="w-full bg-gray-50 border border-gray-200 rounded-xl py-3 pr-11 pl-4 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all text-left"
                                                dir="ltr"
                                            />
                                        </div>
                                        <div className="relative">
                                            <FiMapPin className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 z-10" />
                                            <select
                                                value={deliveryForm.cityId ? String(deliveryForm.cityId) : ""}
                                                onChange={(e) => {
                                                    const selected = cities.find(c => String(c.cityId) === String(e.target.value));
                                                    setDeliveryForm(prev => ({
                                                        ...prev,
                                                        cityId: e.target.value,
                                                        cityName: selected?.cityName || prev.cityName || ""
                                                    }));
                                                    localStorage.setItem("cartSavedCityId", e.target.value);
                                                    localStorage.setItem("cartSavedCityName", selected?.cityName || "");
                                                    localStorage.setItem("selectedCityId", e.target.value);
                                                }}
                                                className="w-full bg-gray-50 border border-gray-200 rounded-xl py-3 pr-11 pl-4 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all appearance-none cursor-pointer"
                                            >
                                                <option value="">اختر المحافظة</option>
                                                {cities.map((city) => (
                                                    <option key={city.cityId} value={String(city.cityId)}>{city.cityName}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <textarea
                                            placeholder="العنوان التفصيلي..."
                                            rows="2"
                                            value={deliveryForm.address || ''}
                                            onChange={(e) => setDeliveryForm({ ...deliveryForm, address: e.target.value })}
                                            className="w-full bg-gray-50 border border-gray-200 rounded-xl py-3 px-4 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all resize-none"
                                        ></textarea>
                                    </div>
                                </div>

                                {/* كوبونات الخصم */}
                                <div className="bg-white rounded-2xl p-4 sm:p-6 shadow-sm border border-gray-100">
                                    <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2 uppercase tracking-wide">
                                        <FiTag className={`${theme.text}`} />
                                        كوبونات الخصم
                                    </h3>

                                    <form onSubmit={handleApplyCoupon} className="flex flex-col sm:flex-row gap-2">
                                        <div className="relative flex-1">
                                            <input
                                                type="text"
                                                value={couponCode || ''}
                                                onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                                                placeholder="أدخل كود الخصم"
                                                disabled={!!appliedCoupon}
                                                className="w-full bg-gray-50 border border-gray-200 rounded-xl py-3 px-4 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all"
                                            />
                                            {appliedCoupon && (
                                                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-green-500 text-xs font-bold">✓</span>
                                            )}
                                        </div>
                                        <button
                                            type="submit"
                                            disabled={!!appliedCoupon || !(couponCode?.trim())}
                                            className={`${appliedCoupon ? 'bg-gray-200 text-gray-500' : theme.bg + ' text-white hover:shadow-lg'} px-6 py-3 rounded-xl text-sm font-bold uppercase tracking-wider transition-all active:scale-95 disabled:opacity-50`}
                                        >
                                            تطبيق
                                        </button>
                                    </form>

                                    {couponError && <p className="text-xs text-red-500 font-bold px-2 mt-2">{couponError}</p>}

                                    {!appliedCoupon && displayCoupons?.length > 0 && (
                                        <div className="pt-3 flex flex-wrap gap-2">
                                            {displayCoupons.slice(0, 3).map(c => (
                                                <button
                                                    key={c.id || c.code}
                                                    type="button"
                                                    onClick={(e) => { e.preventDefault(); setCouponCode(c.code); handleApplyCoupon(e); }}
                                                    className="text-[11px] font-bold px-3 py-1.5 rounded-full border border-gray-200 bg-gray-50 text-gray-600 hover:text-gray-800 hover:border-gray-300 hover:bg-white transition-all shadow-sm block"
                                                >
                                                    {c.code}
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                {/* ملخص الطلب */}
                                <div className="bg-white rounded-2xl p-4 sm:p-6 shadow-sm border border-gray-100 relative overflow-hidden">
                                    <div className={`absolute top-0 left-0 w-full h-1 ${theme.bg}`}></div>
                                    <h3 className="text-lg font-bold text-gray-800 mb-4 uppercase tracking-wide">ملخص الطلب</h3>
                                    <div className="space-y-3 mb-4">
                                        <div className="flex justify-between items-center text-sm">
                                            <span className="text-gray-500">المجموع الفرعي</span>
                                            <span className="font-bold text-gray-800">{formatNumber(subtotal)} د.ع</span>
                                        </div>
                                        {appliedCoupon && (
                                            <div className="flex justify-between items-center text-sm text-green-600">
                                                <span>الخصم ({appliedCoupon.code})</span>
                                                <span className="font-bold">-{formatNumber(couponDiscount)} د.ع</span>
                                            </div>
                                        )}
                                        <div className="flex justify-between items-center text-sm">
                                            <span className="text-gray-500">التوصيل</span>
                                            <span className="font-bold text-gray-800">
                                                {!deliveryForm.cityId ? (
                                                    <span className="text-yellow-600 text-[10px]">يرجى اختيار المحافظة</span>
                                                ) : shipping === 0 ? (
                                                    <span className="text-green-600 font-black text-xs">مجاني ✨</span>
                                                ) : (
                                                    `${formatNumber(shipping)} د.ع`
                                                )}
                                            </span>
                                        </div>
                                        <div className="pt-4 border-t border-gray-100 flex justify-between items-end">
                                            <span className="text-gray-800 font-bold uppercase tracking-wider">الإجمالي</span>
                                            <div className="text-left">
                                                <span className={`text-3xl font-black ${theme.text}`}>{formatNumber(total)}</span>
                                                <span className="text-xs font-bold mr-1 uppercase text-gray-500">د.ع</span>
                                            </div>
                                        </div>
                                    </div>

                                    <button
                                        onClick={() => {
                                            if (!localStorage.getItem("storeCustomerId")) {
                                                setShowAuthModal(true);
                                            } else {
                                                handleCheckoutSubmit();
                                            }
                                        }}
                                        className={`w-full ${theme.bg} text-white py-4 rounded-xl font-bold text-lg shadow-lg hover:shadow-xl transition-all active:scale-95 flex items-center justify-center gap-3`}
                                    >
                                        <span>إتمام الطلب</span>
                                        <FiArrowRight className="rotate-180" />
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default CartSidebar;
